import { apiBasePath } from '../baseUrl';
import { apiUrl } from '../../utils/api';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';

const normalizeSingleProductName = (value) => {
  if (Array.isArray(value)) {
    const firstNonEmpty = value.find((item) => String(item ?? '').trim());
    return String(firstNonEmpty ?? '').trim();
  }

  return String(value ?? '').trim();
};

const normalizeCreatedByIds = (createdBy) => {
  const sourceValues = Array.isArray(createdBy)
    ? createdBy
    : typeof createdBy === 'string'
    ? createdBy.split(',')
    : createdBy === null || createdBy === undefined
    ? []
    : [createdBy];

  return Array.from(
    new Set(
      sourceValues
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
        .filter((value) => /^\d+$/.test(value)),
    ),
  );
};

const normalizeCurrencyIds = (currencyIds) => {
  const sourceValues = Array.isArray(currencyIds)
    ? currencyIds
    : typeof currencyIds === 'string'
    ? currencyIds.split(',')
    : currencyIds === null || currencyIds === undefined
    ? []
    : [currencyIds];

  return Array.from(
    new Set(
      sourceValues
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
        .filter((value) => /^\d+$/.test(value)),
    ),
  );
};

const buildBaseQueryString = (page, perPage, status) => {
  const params = new URLSearchParams({
    perPage: String(perPage || DEFAULT_PER_PAGE),
    page: String(page || DEFAULT_PAGE),
  });
  if (status) {
    params.set('status', status);
  }
  return params.toString();
};

export const buildApprovedPostingsQueryString = ({
  page,
  perPage,
  x3ProductName,
  x3ProductNames,
  createdBy,
  startDate,
  endDate,
  search,
  sortBy,
  sortOrder,
}) => {
  const params = new URLSearchParams();
  params.set('page', String(page || DEFAULT_PAGE));
  params.set('perPage', String(perPage || DEFAULT_PER_PAGE));
  const normalizedProductNames = Array.isArray(x3ProductNames)
    ? x3ProductNames.map((name) => String(name).trim().toLowerCase()).filter(Boolean)
    : typeof x3ProductNames === 'string'
    ? x3ProductNames
        .split(',')
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean)
    : [];
  const uniqueProductNames = Array.from(new Set(normalizedProductNames));

  if (uniqueProductNames.length > 0) {
    const joinedProductNames = uniqueProductNames.join(',');
    params.set('x3ProductNames', joinedProductNames);
  } else if (x3ProductName) {
    const normalizedProductName = normalizeSingleProductName(x3ProductName).toLowerCase();
    if (normalizedProductName) {
      params.set('x3ProductNames', normalizedProductName);
    }
  }
  const normalizedCreatedByIds = normalizeCreatedByIds(createdBy);
  if (normalizedCreatedByIds.length > 0) {
    params.set('createdBy', normalizedCreatedByIds.join(','));
  }
  if (startDate) params.set('startDate', String(startDate));
  if (endDate) params.set('endDate', String(endDate));
  if (search) params.set('search', String(search).trim());
  if (sortBy) {
    const order = sortOrder?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
    params.set('sort', `${sortBy}:${order}`);
  }
  return params.toString();
};

export const extendedApi = apiBasePath.injectEndpoints({
  endpoints: (build) => ({
    getWeeklyGLPostings: build.query({
      query: (args) => {
        const { page, perPage, searchParams = '', status } = args || {};
        const baseParams = buildBaseQueryString(page, perPage, status);
        const queryString = [baseParams, searchParams].filter(Boolean).join('&');
        return `${apiUrl.weeklyGLPosting}?${queryString}`;
      },
      transformResponse: (response) => ({
        glPostings: response?.data?.postings ?? [],
        pagination: response?.pagination ??
          response?.data?.pagination ?? {
            total: 0,
            page: DEFAULT_PAGE,
            perPage: DEFAULT_PER_PAGE,
          },
      }),
      providesTags: ['WeeklyGLPostings'],
    }),
    getWeeklyGLPostingById: build.query({
      query: (id) => {
        const sanitizedId = encodeURIComponent(String(id ?? '').trim());
        return `${apiUrl.weeklyGLPosting}/${sanitizedId}`;
      },
      transformResponse: (response) => {
        const payload = response?.data;
        if (!payload) return null;

        const posting = payload?.posting;
        if (posting && typeof posting === 'object') {
          // Keep existing top-level posting shape for current consumers,
          // while attaching summary/warnings needed for detail breakdowns.
          return {
            ...posting,
            posting,
            summary: payload?.summary,
          };
        }

        return payload;
      },
      providesTags: (result, error, id) => [{ type: 'WeeklyGLPosting', id }],
    }),
    fetchWeeklyGLPostingSublocations: build.query({
      query: ({ x3ProductName, startDate, endDate, currencyIds }) => {
        const normalizedX3ProductName = normalizeSingleProductName(x3ProductName).toUpperCase();
        const normalizedCurrencyIds = normalizeCurrencyIds(currencyIds);
        const params = new URLSearchParams();
        if (normalizedX3ProductName) params.set('x3ProductName', normalizedX3ProductName);
        if (normalizedCurrencyIds.length > 0) {
          params.set('currencyIds', normalizedCurrencyIds.join(','));
        }
        if (startDate) params.set('startDate', String(startDate));
        if (endDate) params.set('endDate', String(endDate));
        const queryString = params.toString();
        return queryString
          ? `${apiUrl.weeklyGLPosting}/fetch-sublocations?${queryString}`
          : `${apiUrl.weeklyGLPosting}/fetch-sublocations`;
      },
      transformResponse: (response) => ({
        sublocations: response?.data?.sublocations ?? [],
        x3ProductName: response?.data?.x3ProductName ?? '',
        isAllPosted: response?.data?.isAllPosted ?? false,
      }),
      providesTags: ['WeeklyGLPostingSublocations'],
    }),
    getApprovedWeeklyGLPostings: build.query({
      query: (args) => {
        const queryString = buildApprovedPostingsQueryString(args || {});
        return `${apiUrl.weeklyGLPosting}?${queryString}`;
      },
      transformResponse: (response) => ({
        postings: response?.data?.postings ?? [],
        pagination: response?.pagination ??
          response?.data?.pagination ?? {
            total: 0,
            page: DEFAULT_PAGE,
            perPage: DEFAULT_PER_PAGE,
            pages: 0,
          },
      }),
      providesTags: ['ApprovedWeeklyGLPostings'],
    }),
    createWeeklyGLPosting: build.mutation({
      query: (postingData) => {
        const normalizedX3ProductName = normalizeSingleProductName(postingData?.x3ProductName);
        return {
          url: apiUrl.weeklyGLPosting,
          method: 'POST',
          body: {
            ...postingData,
            x3ProductName: normalizedX3ProductName,
          },
        };
      },
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: ['WeeklyGLPostings', 'ApprovedWeeklyGLPostings', 'WeeklyGLPostingSublocations'],
    }),
    fetchWeeklyGLPostingFeeds: build.mutation({
      query: (payload) => {
        const normalizedX3ProductName = normalizeSingleProductName(payload?.x3ProductName);
        return {
          url: `${apiUrl.weeklyGLPosting}/feeds`,
          method: 'POST',
          body: {
            ...payload,
            x3ProductName: normalizedX3ProductName,
          },
        };
      },
      transformResponse: (response) => response ?? null,
    }),
    updateWeeklyGLPosting: build.mutation({
      query: ({ id, ...updateData }) => ({
        url: `${apiUrl.weeklyGLPosting}/${encodeURIComponent(String(id))}`,
        method: 'PUT',
        body: updateData,
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: (result, error, { id }) => [
        { type: 'WeeklyGLPosting', id },
        'WeeklyGLPostings',
        'ApprovedWeeklyGLPostings',
      ],
    }),
    deleteWeeklyGLPosting: build.mutation({
      query: (id) => ({
        url: `${apiUrl.weeklyGLPosting}/${encodeURIComponent(String(id))}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WeeklyGLPostings', 'ApprovedWeeklyGLPostings'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWeeklyGLPostingByIdQuery,
  useLazyFetchWeeklyGLPostingSublocationsQuery,
  useGetApprovedWeeklyGLPostingsQuery,
  useCreateWeeklyGLPostingMutation,
  useFetchWeeklyGLPostingFeedsMutation,
  useUpdateWeeklyGLPostingMutation,
  useDeleteWeeklyGLPostingMutation,
} = extendedApi;
